const CAP_BLOCKCHAIN_INTRO = {
  "id": 60,
  "title": "¿Qué es la tecnología Blockchain?",
  "slug": "que-es-la-tecnologia-blockchain",
  "type": "lesson",
  "category": "blockchain",
  "level": "basic",
  "sections": [
    {
      "heading": "La parábola del Suricato Volador",
      "text": [
        "Para entender qué es blockchain de forma rápida, imagina que vas caminando por la calle y, de repente, un suricato volador gigantesco aterriza en una plaza llena de gente, se come todos los helados de los niños, lanza dos grandes chillidos y se marcha igual de rápido que llegó.",
        "Después del evento, se coloca un detector de mentiras a las 1.000 personas que lo presenciaron y se registra exactamente lo que han visto.",
        "Todas coinciden en la misma historia, con los mismos detalles. El resultado es un consenso masivo sobre un hecho que nadie puede negar.",
        "Esta idea de muchas partes independientes confirmando la misma versión de la realidad es la base del funcionamiento de una blockchain.",
        "¿Habría alguna duda acerca de lo que ocurrió?"
      ]
    },
    {
      "heading": "Blockchain: una tecnología que puede cambiarlo todo",
      "text": [
        "La cadena de bloques o blockchain es una tecnología que puede transformar por completo nuestra relación con el mundo digital.",
        "Imagina un fichero de texto con dos columnas: en la primera hay un identificador (por ejemplo, 'abc') y en la segunda un número (por ejemplo, '34'). Es decir, a 'abc' le corresponde '34'.",
        "Ahora imagina que ese fichero está replicado en miles de ordenadores de todo el mundo, y que nadie puede modificarlo sin que todos se den cuenta.",
        "Cuando hay que actualizar algo legítimamente, todos los nodos se sincronizan en cuestión de segundos.",
        "Aunque uno de esos ordenadores desaparezca, ¿el sistema dejaría de funcionar?",
        "No, porque blockchain permite un registro distribuido, resistente a fallos y manipulaciones, sin necesidad de confiar en una autoridad central.",
        "Su magia se apoya en varias piezas: criptografía, redes P2P, teoría de juegos y mecanismos de consenso."
      ]
    },
    {
      "heading": "Un libro mayor digital distribuido",
      "text": [
        "¿Qué es una blockchain en esencia?",
        "Es un registro o libro mayor de eventos digitales distribuido entre muchas partes.",
        "Solo puede actualizarse si la mayoría de los participantes del sistema llegan a un consenso, y una vez escrita, la información ya no puede borrarse.",
        "La blockchain de Bitcoin, por ejemplo, contiene un registro verificable de todas las transacciones que se han realizado desde su inicio.",
        "Esto permite tener un historial transparente, resistente a la censura y muy difícil de falsificar."
      ]
    },
    {
      "heading": "Explicación de la parábola del Suricato Volador",
      "text": [
        "En la analogía del suricato volador, cada persona que vio el suceso representa un nodo en la red.",
        "Esos nodos están separados geográfica y computacionalmente, pero todos coinciden en el mismo relato de lo ocurrido.",
        "El 'detector de mentiras' equivale a la Prueba de Trabajo (Proof of Work), un proceso criptográfico que demuestra que un ordenador ha resuelto correctamente un problema.",
        "¿Qué tan difícil sería falsificar una entrada en la blockchain?",
        "Sería como lograr que más de la mitad de la gente se pusiera de acuerdo para mentir exactamente lo mismo al mismo tiempo, sin coordinarse. En la práctica, es un ataque extremadamente difícil.",
        "Lo que se escribe en la cadena de bloques no desaparece: la blockchain es un registro inmutable y permanente, una base de datos que solo permite añadir información, nunca borrarla o modificarla sin consenso."
      ]
    },
    {
      "heading": "Del mundo centralizado al consenso distribuido",
      "text": [
        "Hoy en día, casi todas las interacciones online dependen de una autoridad central en la que confiamos: bancos, plataformas de mensajería, redes sociales, proveedores de servicios, etc.",
        "Confiamos en que el banco nos muestra correctamente el saldo, que WhatsApp entrega nuestros mensajes, que el antivirus nos informa bien del estado de nuestro ordenador.",
        "Pero ¿siempre podemos confiar en que esa información es verdadera y segura?",
        "Siempre existe el riesgo de que un proveedor mienta, se equivoque o sea hackeado.",
        "Con blockchain, la confianza ya no está en una sola entidad, sino en todo un conjunto de nodos que verifican colectivamente."
      ]
    },
    {
      "heading": "Bitcoin como dinero digital basado en blockchain",
      "text": [
        "Cuando pensamos en cosas digitales, normalmente pensamos en archivos que se pueden copiar y pegar: fotos, canciones, documentos, etc.",
        "Pero ¿tendría sentido que el dinero pudiera copiarse y pegarse infinitamente?",
        "Bitcoin resuelve este problema usando la blockchain como libro de contabilidad global.",
        "No existen 'archivos' de bitcoin como tal. Lo que existe es un registro público que indica cuánto corresponde a cada dirección en cada momento.",
        "La blockchain lleva de forma impecable las cifras de todo el sistema, impidiendo el doble gasto y permitiendo que el dinero digital no se pueda duplicar arbitrariamente."
      ]
    },
    {
      "heading": "Un libro escrito por miles de participantes",
      "text": [
        "¿Podría existir un libro contable global, transparente, seguro y sin dueño?",
        "Por primera vez en la historia, estamos escribiendo un libro de contabilidad de forma colaborativa entre miles de participantes independientes.",
        "Sin censura, sin una autoridad central, validado por todos.",
        "La transparencia y la resistencia a la manipulación podrían cambiar completamente cómo se gestionan la información, el valor y los acuerdos."
      ]
    },
    {
      "heading": "Consenso distribuido y privacidad",
      "text": [
        "Gracias al consenso distribuido, es posible crear un registro incorruptible de eventos pasados y presentes en el mundo digital.",
        "¿Se puede verificar que algo ocurrió sin revelar todos los datos?",
        "Sí. Se puede demostrar que un evento fue válido, sin revelar quién lo hizo ni qué información había detrás.",
        "Esto explica por qué Bitcoin, a pesar de tener un libro mayor público, permite cierto nivel de privacidad cuando se usa correctamente."
      ]
    },
    {
      "heading": "Malentendidos típicos acerca de Blockchain",
      "text": [
        "Al ser una tecnología nueva, es normal que existan preguntas frecuentes y confusiones."
      ],
      "list": [
        "“¿Bitcoin es una red descentralizada?” En realidad, es una red distribuida P2P resistente a fallos.",
        "“¿Si un nodo falla, la red deja de funcionar?” No. La información fluye por caminos alternativos.",
        "“¿Blockchain y Blockchain.com son lo mismo?” No. Blockchain es la tecnología; Blockchain.com es un explorador y servicio."
      ]
    },
    {
      "heading": "Potencial de la tecnología blockchain",
      "text": [
        "¿Blockchain tiene potencial más allá de Bitcoin?",
        "Muchas figuras del mundo tecnológico comparan su impacto con la llegada del ordenador personal o Internet.",
        "Una de sus aplicaciones más relevantes son los contratos inteligentes o smart contracts.",
        "Un contrato inteligente es un acuerdo codificado que se ejecuta automáticamente cuando se cumplen ciertas condiciones, sin necesidad de intermediarios.",
        "Esto abre un mundo de posibilidades: pagos seguros, automatización, transparencia, confianza digital y mucho más.",
        "¿Te imaginas un futuro donde todo esto sea parte de nuestra vida diaria? Ese futuro ya está en camino."
      ]
    }
  ]
};

export default CAP_BLOCKCHAIN_INTRO;
